// LT-412: Agent detail page — capability matrix removed (parked
// pending Voice Gateway tool-call support per LT-405 §7), version
// timeline collapsed to a compact "vN (current)" pill + expandable
// dated list (no diff badges), "Pinned by" renamed to "Used by"
// (collapsed by default), and the new L1 persona sections (Voice,
// Tone, Agent persona, Objection-handling style, Prompt template)
// surface their values for read.
//
// All editing now goes through the unified Agent edit form at
// `/agents/:agentId/edit` (LT-411). System presets render read-only
// and the Edit CTA is hidden for them.
//
// Route: /agents/:agentId
//
// Permission gates per LT-338 LLD §6:
//   - System preset: read-only.
//   - Workspace custom, admin OR owner: Edit affordance shown.
//   - Workspace custom, other non-admin: read-only.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Pencil,
} from 'lucide-react';

import PageLoader from '../../components/PageLoader';
import PageShell from '../../components/PageShell';
import { Button, Card, Pill } from '../../ui';
import { useAuth } from '../../context/AuthContext';
import { Agent, AgentPinnedByStep, AgentVersion } from '../../types/agent';
import agentsService from '../../services/agentsService';

const AgentDetailPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // LT-412: version + used-by data load lazily and stay collapsed by
  // default. The agent's current persona fields are the primary surface;
  // history is a secondary affordance.
  const [versions, setVersions] = useState<AgentVersion[] | null>(null);
  const [usedBy, setUsedBy] = useState<AgentPinnedByStep[] | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [showUsedBy, setShowUsedBy] = useState(false);

  const reload = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const fetched = await agentsService.get(agentId);
      setAgent(fetched);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load agent');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const loadTimeline = useCallback(async () => {
    if (!agentId) return;
    setTimelineError(null);
    try {
      const [v, p] = await Promise.all([
        agentsService.listVersions(agentId),
        agentsService.listPinnedBy(agentId),
      ]);
      setVersions(v);
      setUsedBy(p);
    } catch (err) {
      setTimelineError(err instanceof Error ? err.message : 'Failed to load timeline');
    }
  }, [agentId]);

  useEffect(() => {
    if (agent) void loadTimeline();
  }, [agent, loadTimeline]);

  const canEdit = useMemo(() => {
    if (!agent) return false;
    if (agent.is_system) return false;
    if (agent.deprecated_at) return false;
    if (isAdmin()) return true;
    return Boolean(user?.id && agent.created_by_user_id === user.id);
  }, [agent, isAdmin, user]);

  const currentVersionNumber = agent?.current_version?.version_number ?? null;
  const v = agent?.current_version ?? null;

  if (loading) {
    return <PageLoader />;
  }

  if (loadError || !agent) {
    return (
      <PageShell>
        <Card padding="default">
          <div className="flex items-start gap-3 text-danger-text">
            <AlertTriangle className="w-5 h-5 mt-0.5" strokeWidth={2} />
            <div>
              <div className="font-medium">Failed to load agent</div>
              <div className="text-sm text-slate-300">
                {loadError ?? 'Agent not found in this workspace.'}
              </div>
              <Button
                intent="ghost"
                size="sm"
                className="mt-3"
                onClick={() => navigate('/agents')}
              >
                Back to agents
              </Button>
            </div>
          </div>
        </Card>
      </PageShell>
    );
  }

  const voiceLine = (() => {
    const m = v?.voice_id_male ?? null;
    const f = v?.voice_id_female ?? null;
    if (!m && !f) return null;
    const parts: string[] = [];
    if (m) parts.push(`male: ${m}`);
    if (f) parts.push(`female: ${f}`);
    return parts.join(' · ');
  })();

  return (
    <PageShell>
      <div className="mb-4">
        <Link
          to="/agents"
          className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-slate-200"
          data-testid="back-to-agents"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
          Agents
        </Link>
      </div>

      <div className="mb-6 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">
            {agent.category}
          </div>
          <h1
            className="text-2xl font-semibold text-slate-50 mb-1"
            data-testid="agent-name"
          >
            {agent.name}
          </h1>
          <div className="flex gap-2 flex-wrap items-center">
            <Pill intent={agent.is_system ? 'info' : 'accent'}>
              {agent.is_system ? 'System preset' : 'Custom'}
            </Pill>
            {currentVersionNumber !== null && (
              <Pill intent="neutral" data-testid="current-version-badge">
                <span className="font-mono">v{currentVersionNumber}</span>
                <span className="ml-1 text-slate-400">(current)</span>
              </Pill>
            )}
            {agent.deprecated_at && <Pill intent="warn">Deprecated</Pill>}
          </div>
        </div>
        {canEdit && (
          <Button
            intent="primary"
            size="sm"
            onClick={() => navigate(`/agents/${agent.id}/edit`)}
            data-testid="agent-edit-cta"
          >
            <Pencil className="w-4 h-4" strokeWidth={2} />
            <span className="ml-1.5">Edit</span>
          </Button>
        )}
      </div>

      {agent.description && (
        <Card padding="default" className="mb-6">
          <p className="text-sm text-slate-300">{agent.description}</p>
        </Card>
      )}

      {/* LT-412: L1 sections — Voice, Tone, Agent persona, Objection-
          handling style, Prompt template. Read for everyone; the Edit
          CTA above takes editors to /agents/:id/edit. */}
      <Card padding="default" className="mb-6" data-testid="l1-sections">
        <SectionRow
          label="Voice"
          value={voiceLine}
          fallback="No voice configured."
        />
        <SectionRow
          label="Tone"
          value={v?.tone ?? null}
          fallback="No tone set yet."
        />
        <SectionRow
          label="Agent persona"
          value={v?.agent_persona ?? null}
          fallback="No persona set yet."
        />
        <SectionRow
          label="Objection-handling style"
          value={v?.objection_handling_style ?? null}
          fallback="No objection-handling style set yet."
          multiline
        />
        <PromptTemplateRow value={v?.prompt_template ?? ''} />
      </Card>

      {/* LT-412: compact "see version history" — collapsed by default;
          expands to a plain dated list (no diff displays). */}
      <Card padding="default" className="mb-6" data-testid="version-history">
        <button
          type="button"
          onClick={() => setShowVersions((s) => !s)}
          className="flex w-full items-center justify-between gap-2 text-left"
          data-testid="toggle-version-history"
        >
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Version history
            </div>
            <div className="text-sm text-slate-300">
              {versions === null
                ? 'Loading…'
                : versions.length === 1
                ? '1 version since creation'
                : `${versions.length} versions since creation`}
            </div>
          </div>
          {showVersions ? (
            <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={2} />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" strokeWidth={2} />
          )}
        </button>
        {showVersions && (
          <div className="mt-3 pt-3 border-t border-white/[0.08]">
            {timelineError ? (
              <div className="text-sm text-danger-text">{timelineError}</div>
            ) : versions === null ? (
              <div className="text-sm text-slate-400">Loading…</div>
            ) : versions.length === 0 ? (
              <div className="text-sm text-slate-400" data-testid="version-empty">
                No versions yet — created automatically when the agent is edited.
              </div>
            ) : (
              <ul className="space-y-1.5" data-testid="version-list">
                {[...versions].reverse().map((row) => {
                  const isCurrent = agent.current_version?.id === row.id;
                  return (
                    <li
                      key={row.id}
                      className="flex items-center gap-3 text-sm"
                      data-testid={`version-row-v${row.version_number}`}
                    >
                      <span className="font-mono text-slate-300 w-10 shrink-0">
                        v{row.version_number}
                      </span>
                      <span className="text-slate-400">
                        {new Date(row.created_at).toLocaleDateString()}
                      </span>
                      {isCurrent && (
                        <Pill intent="ok" data-testid={`current-pill-v${row.version_number}`}>
                          current
                        </Pill>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </Card>

      {/* LT-412: "Pinned by" renamed to "Used by"; collapsed by default. */}
      <Card padding="default" className="mb-6" data-testid="used-by">
        <button
          type="button"
          onClick={() => setShowUsedBy((s) => !s)}
          className="flex w-full items-center justify-between gap-2 text-left"
          data-testid="toggle-used-by"
        >
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Used by
            </div>
            <div className="text-sm text-slate-300">
              {usedBy === null
                ? 'Loading…'
                : usedBy.length === 0
                ? 'No in-flight steps pin an older version.'
                : `${usedBy.length} in-flight ${
                    usedBy.length === 1 ? 'step pins' : 'steps pin'
                  } a version of this agent`}
            </div>
          </div>
          {showUsedBy ? (
            <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={2} />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" strokeWidth={2} />
          )}
        </button>
        {showUsedBy && (
          <div className="mt-3 pt-3 border-t border-white/[0.08]">
            {timelineError ? (
              <div className="text-sm text-danger-text">{timelineError}</div>
            ) : usedBy === null ? (
              <div className="text-sm text-slate-400">Loading…</div>
            ) : usedBy.length === 0 ? (
              <div className="text-sm text-slate-400" data-testid="used-by-empty">
                No in-flight steps pin an older version.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="used-by-list">
                {usedBy.map((row) => (
                  <li
                    key={row.step_id}
                    className="flex items-center justify-between gap-3 rounded-ds-md bg-surface-2 border border-white/[0.08] px-3 py-2"
                    data-testid={`used-by-row-${row.step_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-200 truncate">
                        <Link
                          to={`/sequences/${row.sequence_id}`}
                          className="hover:text-accent"
                        >
                          {row.sequence_name}
                        </Link>{' '}
                        <span className="text-slate-400 text-xs">
                          · step {row.position} ({row.step_type})
                        </span>
                      </div>
                    </div>
                    <Pill intent="neutral">
                      <span className="font-mono">v{row.agent_version_number}</span>
                    </Pill>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>
    </PageShell>
  );
};

interface SectionRowProps {
  label: string;
  value: string | null;
  fallback: string;
  multiline?: boolean;
}

const SectionRow: React.FC<SectionRowProps> = ({ label, value, fallback, multiline }) => (
  <div className="py-3 border-b border-white/[0.06] last:border-0">
    <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
      {label}
    </div>
    {value ? (
      <div
        className={
          multiline
            ? 'text-sm text-slate-200 whitespace-pre-line'
            : 'text-sm text-slate-200'
        }
      >
        {value}
      </div>
    ) : (
      <div className="text-sm text-slate-500 italic">{fallback}</div>
    )}
  </div>
);

interface PromptTemplateRowProps {
  value: string;
}

const PromptTemplateRow: React.FC<PromptTemplateRowProps> = ({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const trimmed = value.trim();
  const hasContent = trimmed.length > 0;
  const isLong = trimmed.length > 280;
  return (
    <div className="py-3 border-b border-white/[0.06] last:border-0">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-xs uppercase tracking-wide text-slate-400">
          Prompt template
        </div>
        {hasContent && isLong && (
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            className="text-xs text-slate-400 hover:text-slate-200"
            data-testid="prompt-template-toggle"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        )}
      </div>
      {hasContent ? (
        <pre
          className={
            expanded
              ? 'text-sm text-slate-200 whitespace-pre-wrap font-mono'
              : 'text-sm text-slate-200 whitespace-pre-wrap font-mono line-clamp-3'
          }
          data-testid="prompt-template-content"
        >
          {trimmed}
        </pre>
      ) : (
        <div className="text-sm text-slate-500 italic">No prompt template set yet.</div>
      )}
    </div>
  );
};

export default AgentDetailPage;
